import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getConnectedSystemsList } from '../../api/destinations/destinationsApi';
import { IntegrationRoles, loadIntegrationRoleMap } from '../../api/routes/routesApi';
import DestinationsList from './DestinationsList';
import { Destination } from '../common/ObjectTypes';
import { ManageDestinationDetails } from './ManageDestinationDetails';
import { useAllOrganizations } from "../../api/organizations/useAllOrganizations";
import { page_size } from '../../api/apiUtils';
import { useDestinationTypes, useDestinationUrls } from '../../api/useFilterOptions';
import { useListQuery } from '../../api/useListQuery';
import { queryKeys } from '../../api/queryKeys';
import getUserPermissions from '../../api/users/getUserPermissions';
import { useKeycloak } from '@react-keycloak/web';
import { useHelp } from '../../context/HelpContext';
import { useTranslation } from 'react-i18next';

const DestinationsPage = () => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        setHelpContent({
            cards: [{
                title: t('Managing Connections in Gundi'),
                description: t('Learn how to set up the systems Gundi exchanges data with, as sources or as destinations.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections-gundi/connections-in-gundi' },
            }],
        });
    }, [i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);
    type FilterTypes = {
        base_url__in: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultDestinationsFilter = {
        base_url__in: [],
        type__in: [],
        owner__in: [],
        ordering: "name",
        search: ""
    };
    const navigate = useNavigate();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [canAddDestination, setCanAddDestination] = useState(false);
    // Which Routes use each record, and in which role. No endpoint reports this
    // per integration, so it is derived once from the Routes list and reused for
    // every row. The sweep walks every Route, so it is reused for a few minutes
    // across visits; any Route or connection save marks it out of date at once.
    const roleMapQuery = useQuery({
        queryKey: queryKeys.connections.roleMap(),
        queryFn: () => loadIntegrationRoleMap(token as string),
        staleTime: 5 * 60 * 1000,
        enabled: !!token && typeof token === 'string' && !keycloak.isTokenExpired(),
    });
    const roleMap: Record<string, IntegrationRoles> = roleMapQuery.data ?? {};
    // Until the sweep lands, a record's roles are unknown — which is not the same as
    // "not in use", so the chips wait rather than claiming something false.
    const roleMapLoaded = roleMapQuery.data !== undefined;
    const [roleFilter, setRoleFilter] = useState<{ label: string, value: string } | null>(null);
    // The ids a role resolves to, from the Routes sweep. null = no role filter.
    const roleFilterIds: string[] | null = !roleFilter ? null : Object.keys(roleMap).filter((id) => {
        const roles = roleMap[id];
        return roleFilter.value === "source" ? roles.asProvider.length > 0 : roles.asDestination.length > 0;
    });
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('destinationFilter');
        return savedFilter ? { ...defaultDestinationsFilter, ...JSON.parse(savedFilter) } : defaultDestinationsFilter;
    });
    const [offset, setOffset] = useState<number>(0);
    const { organizations, loadingOrganizations } = useAllOrganizations();
    const { destinationTypes, loadingDestinationTypes } = useDestinationTypes({ allTypes: true });
    const { destinationUrls, loadingDestinationUrls } = useDestinationUrls();
    const [destDetailsModal, setDestDetailsModal] = useState(false);

    const destinationFilterQuery = useMemo(() => ({
        base_url__in: destinationFilter.base_url__in.map(a => a.id).toString(),
        type__in: destinationFilter.type__in.map(a => a.id).toString(),
        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
        ordering: destinationFilter.ordering,
        search: destinationFilter.search,
        offset: offset,
        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size),
        // Role is not a field the API filters on, but it resolves to a set of
        // ids the Routes sweep already gave us — pushed down as id__in so
        // the server pages the real result set instead of the browser hiding
        // rows from one page.
        ...(roleFilterIds === null ? {} : { id__in: roleFilterIds.join(",") }),
    }), [destinationFilter, offset, roleFilterIds]);
    const connectionsList = useListQuery<Destination>(
        queryKeys.connections.list(destinationFilterQuery),
        // No ids means no matches, so don't ask.
        async (token) => roleFilterIds !== null && roleFilterIds.length === 0
            ? { results: [], count: 0 }
            : getConnectedSystemsList(token, destinationFilterQuery),
    );
    // Settles on the first answer even if it failed, so the page never hangs on
    // its skeleton.
    const pageLoaded = connectionsList.settled;

    useEffect(() => {
        sessionStorage.setItem('destinationFilter', JSON.stringify(destinationFilter));
    }, [destinationFilter]);

    useEffect(() => {
        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddDestination(temp);
                } catch (err) {
                    console.log(err);
                }
            }
        }
        loadPerms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleAddDestinationPage = () => {
        navigate('/connections/new');
    }

    const handleToggleDestinationDetailsModal = async (dest_id?: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            navigate(`/connections/${dest_id}/configuration`, { replace: false });
        }
        setDestDetailsModal(!destDetailsModal);
        if (destDetailsModal) connectionsList.refresh();
    }

    const handleGlobalSearch = (searchSubStr: string) => {
        setDestinationFilter(prev => ({ ...prev, search: searchSubStr }));
        setOffset(0);
    }

    const handleFilterEndpoint = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, base_url__in: tags }));
        setOffset(0);
    }

    const handleFilterType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        setOffset(0);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        setOffset(0);
    }

    const handleSortBy = (selected: { label: string, value: string }) => {
        setDestinationFilter(prev => ({ ...prev, ordering: selected.value }));
        setOffset(0);
    }

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const endpointSelectValue = toSingleValue(destinationFilter.base_url__in);
    const typeSelectValue = toSingleValue(destinationFilter.type__in);
    const ownerSelectValue = toSingleValue(destinationFilter.owner__in);

    return (
            <div>
                {destDetailsModal &&
                    <ManageDestinationDetails />
                }
                <DestinationsList
                    canAddDestination={canAddDestination}
                    reloadDestinations={connectionsList.loading}
                    destinations={connectionsList.results}
                    roleFilterValue={roleFilter}
                    onChangeRole={(selected) => {
                        setRoleFilter(selected);
                        setOffset(0);
                    }}
                    roleFilterDisabled={!roleMapLoaded}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    destinationUrls={destinationUrls}
                    loadingDestinationUrls={loadingDestinationUrls}
                    organizations={organizations}
                    loadingOrganizations={loadingOrganizations}
                    onAddDestination={handleAddDestinationPage}
                    onViewDetails={handleToggleDestinationDetailsModal}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onRefresh={() => { connectionsList.refresh(); roleMapQuery.refetch(); }}
                    globalSearchStr={destinationFilter.search}
                    ordering={destinationFilter.ordering}
                    onChangeSortBy={handleSortBy}
                    onChangeEndpoint={handleFilterEndpoint}
                    endpointFilterValue={endpointSelectValue}
                    onChangeType={handleFilterType}
                    typeFilterValue={typeSelectValue}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    onPageClicked={handlePageClick}
                    totalCount={connectionsList.count}
                    pageLoaded={pageLoaded}
                    take={page_size}
                    offset={offset}
                />
            </div>
        );
};

export default DestinationsPage;
